import { TLeadStatus, TLeadStatusChanges } from "@/models/leads.model";
import leadsService from "@/services/leads.service";
import { TRequestStatus } from "@models/msc.model";
import { Dispatch, PayloadAction, createSlice } from "@reduxjs/toolkit";
import { RootState } from "..";

type LeadsState = {
  initialized: boolean;
  status: TRequestStatus;
  list: TLeadStatus[];
};

//-----THUNK FUNCTIONS-----

const initialState: LeadsState = {
  initialized: false,
  status: "IDLE",
  list: [],
};

const leadStatusListSlice = createSlice({
  name: "leadStatusList",
  initialState,
  reducers: {
    startLoading(state) {
      state.status = "PENDING";
    },
    gotStatusList(state, action: PayloadAction<TLeadStatus[]>) {
      state.initialized = true;
      state.status = "IDLE";
      state.list = action.payload;
    },
    savedLeadStatus(state, action: PayloadAction<TLeadStatus>) {
      state.list.push(action.payload);
    },
    updatedLeadStatus(state, action: PayloadAction<TLeadStatus>) {
      const index = state.list.findIndex((status) => status.id === action.payload.id);
      if (index === -1) return;
      // PATCH /lead/status/:id answers with the bare row, without the computed
      // `totalLeads`. Merging (instead of replacing) keeps the count we already
      // have — a rename or recolor doesn't move any lead.
      const current = state.list[index];
      state.list[index] = {
        ...current,
        ...action.payload,
        totalLeads: action.payload.totalLeads ?? current.totalLeads,
      };
    },
    leadStatusRemoved(state, action: PayloadAction<number>) {
      state.list = state.list.filter((status) => status.id !== action.payload);
    },
    totalLeadsUpdated(state, action: PayloadAction<{ oldStatus: number; newStatus: number }>) {
      state.list = state.list.map((item) => {
        if (item.id === action.payload.oldStatus) return { ...item, totalLeads: item.totalLeads - 1 };
        if (item.id === action.payload.newStatus) return { ...item, totalLeads: item.totalLeads + 1 };
        return item;
      });
    },
  },
});

// ----------------------------

export const getLeadStatusList = () => async (dispatch: Dispatch, getState: () => RootState) => {
  const { leadStatusList } = getState();
  const { list, initialized } = leadStatusList;

  // if already cached, return directly
  if (initialized) return list;

  dispatch(leadStatusListSlice.actions.startLoading());

  const response = await leadsService.getLeadStatusList();
  dispatch(leadStatusListSlice.actions.gotStatusList(response));
  return response;
};

// ----------------------------

/**
 * Force-refetches the catalog, bypassing the `initialized` cache. Needed after
 * a reorder or a delete: both rewrite the `order` of every remaining status
 * server-side, and a delete moves lead counts onto the reassignment target.
 */
export const reloadLeadStatusList = () => async (dispatch: Dispatch) => {
  const response = await leadsService.getLeadStatusList();
  dispatch(leadStatusListSlice.actions.gotStatusList(response));
  return response;
};

// ----------------------------

export const saveLeadStatus = (data: Pick<TLeadStatus, "name" | "color">) => async (dispatch: Dispatch) => {
  const response = await leadsService.saveLeadStatus(data);
  dispatch(leadStatusListSlice.actions.savedLeadStatus(response));
  return response;
};

export const updateLeadStatus =
  (id: number, changes: TLeadStatusChanges) => async (dispatch: Dispatch) => {
    const response = await leadsService.updateLeadStatus({ id, ...changes });
    // A reorder rewrites every status' `order`, so the single-item patch isn't
    // enough — pull the whole list back instead.
    if (changes.order !== undefined) return reloadLeadStatusList()(dispatch);
    dispatch(leadStatusListSlice.actions.updatedLeadStatus(response));
    return response;
  };

export const deleteLeadStatus = (id: number, newStatusId?: number) => async (dispatch: Dispatch) => {
  await leadsService.deleteLeadStatus(id, newStatusId);
  dispatch(leadStatusListSlice.actions.leadStatusRemoved(id));
  // Reassigned leads changed the counts and the backend resequenced `order`.
  return reloadLeadStatusList()(dispatch);
};

export const updateTotalLeads = (data: { oldStatus: number; newStatus: number }) => (dispatch: Dispatch) => {
  dispatch(leadStatusListSlice.actions.totalLeadsUpdated(data));
};

export default leadStatusListSlice.reducer;
